import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Sparkles } from 'lucide-react';
import { fireEvent } from '../utils/funnelEvents';

// Above-the-fold conversion block for generated content pages.
//
// The glossary's only CTA used to sit ~83% down a 668-line template, and over
// the 4 days of beacon data we have it was clicked 9 times across all 421
// pages. This block sits directly under the definition, where the reader
// still is, and scales its weight to the search intent behind the slug (see
// utils/contentIntent.js): a definition reader gets one line, someone who
// landed while comparing organizers gets an actual product answer.
//
// Every variant links to /download rather than firing a direct binary hit, so
// the existing /download page keeps ownership of platform detection, the
// consent-gated ad pixels, and the `download` beacon event the funnel is
// already measured on. The cta_id is per-variant so the next funnel cut can
// tell which intent tier actually moved.

const VARIANTS = {
  commercial: {
    eyebrow: 'Looking for a tool?',
    heading: 'Sortio does this for you',
    body: 'Describe how you want files organized in plain English. Sortio proposes the moves, you review the plan, then apply. Nothing moves until you approve it, and any sort can be undone.',
    points: [
      'Works on Mac and Windows',
      'Preview every move before it happens',
      'One-click undo on any sort',
    ],
    secondary: { label: 'Compare Sortio to other organizers', href: '/compare' },
  },
  task: {
    eyebrow: 'Do it automatically',
    heading: 'Let Sortio handle this pass',
    body: 'Rather than doing this by hand, describe the result you want and let Sortio propose the moves. You review the plan before anything is applied, and every sort can be undone.',
    points: [
      'Works on Mac and Windows',
      'Review the plan before anything moves',
    ],
    secondary: null,
  },
  definitional: {
    eyebrow: null,
    heading: 'Put this into practice',
    body: 'Sortio applies the idea below to a real folder: describe how you want files organized, review the proposed moves, then apply. Rule-based sorting is free and unlimited.',
    points: [],
    secondary: null,
  },
};

const useDetectedOS = () => {
  const [os, setOS] = useState(null);
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    // Keep the prerendered HTML on the generic label so react-snap does not
    // bake one platform's copy into every static page.
    if (navigator.userAgent === 'ReactSnap') return;
    const ua = navigator.userAgent || '';
    if (/Macintosh|Mac OS X/i.test(ua)) setOS('mac');
    else if (/Windows/i.test(ua)) setOS('windows');
  }, []);
  return os;
};

const GlossaryCTA = ({ intent = 'definitional', page, placement = 'hero' }) => {
  const detectedOS = useDetectedOS();
  const variant = VARIANTS[intent] || VARIANTS.definitional;
  const ctaId = `glossary_${placement}_${intent}`;

  const buttonLabel =
    detectedOS === 'mac'
      ? 'Download Sortio for Mac'
      : detectedOS === 'windows'
        ? 'Download Sortio for Windows'
        : 'Download Sortio Free';

  const isLight = intent === 'definitional';

  return (
    <div
      className={
        isLight
          ? 'bg-apple-700/20 border border-apple-600/30 rounded-2xl p-5 mb-8'
          : 'bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 border border-sortio-primary/20 rounded-2xl p-6 mb-8'
      }
    >
      {variant.eyebrow && (
        <div className="flex items-center mb-2">
          <Sparkles className="text-sortio-primary mr-2 flex-shrink-0" size={16} />
          <span className="text-sm font-medium text-sortio-primary">{variant.eyebrow}</span>
        </div>
      )}

      <h2 className={isLight ? 'text-lg font-semibold text-apple-50 mb-2' : 'text-xl font-semibold text-apple-50 mb-3'}>
        {variant.heading}
      </h2>

      <p className="text-apple-300 leading-relaxed mb-4">{variant.body}</p>

      {variant.points.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2 mb-5">
          {variant.points.map((point) => (
            <div key={point} className="flex items-start space-x-2">
              <CheckCircle className="text-green-400 mt-0.5 flex-shrink-0" size={15} />
              <span className="text-sm text-apple-300">{point}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <Link
          to="/download"
          onClick={() =>
            fireEvent('cta_click', {
              cta_id: ctaId,
              page,
              target: '/download',
              intent,
              detected_os: detectedOS || 'unknown',
            })
          }
          className={
            isLight
              ? 'inline-flex items-center px-5 py-2.5 bg-sortio-primary hover:bg-sortio-primary/90 text-white text-sm font-medium rounded-xl transition-colors duration-200'
              : 'inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200'
          }
        >
          {buttonLabel}
          <ArrowRight className="ml-2" size={16} />
        </Link>

        {variant.secondary && (
          <Link
            to={variant.secondary.href}
            onClick={() =>
              fireEvent('cta_click', {
                cta_id: `${ctaId}_secondary`,
                page,
                target: variant.secondary.href,
                intent,
              })
            }
            className="text-sm text-apple-300 hover:text-sortio-primary transition-colors underline underline-offset-4"
          >
            {variant.secondary.label}
          </Link>
        )}
      </div>
    </div>
  );
};

export default GlossaryCTA;
